import React from "react"
import styled from "styled-components"
import { up } from "styled-breakpoints"
import colors from "@constants/colors"
import { Close } from "@components/icons"

interface FloatingPromoBannerProps {
  imageSrc?: string
  mobileImageSrc?: string
  imageSrcs?: string[]
  mobileImageSrcs?: string[]
  alt?: string
  sessionKey?: string
}

const Wrapper = styled.aside`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 12px;
  z-index: 1002;
  width: min(720px, calc(100vw - 24px));
  overflow: hidden;
  background: ${colors.black};
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.28);

  ${up("laptop")} {
    bottom: 16px;
    width: 720px;
  }
` as React.FC<React.HTMLAttributes<HTMLElement>>

const Picture = styled.picture`
  display: block;
  width: 100%;
`

const PromoImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
` as React.FC<React.ImgHTMLAttributes<HTMLImageElement>>

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
` as React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>>

const Dots = styled.div`
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
`

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border: 0;
  border-radius: 999px;
  padding: 0;
  cursor: pointer;
  background: ${({ active }) =>
    active ? colors.snow : "rgba(255, 255, 255, 0.45)"};
`

const FloatingPromoBanner: React.FC<FloatingPromoBannerProps> = ({
  imageSrc,
  mobileImageSrc,
  imageSrcs,
  mobileImageSrcs,
  alt = "Promo banner",
  sessionKey = "floating-promo-banner-dismissed",
}) => {
  const [dismissed, setDismissed] = React.useState(false)
  const [ready, setReady] = React.useState(false)
  const [currentIndex, setCurrentIndex] = React.useState(0)

  const desktopSlides = React.useMemo(() => {
    const list = imageSrcs?.filter(Boolean) || []
    if (list.length > 0) return list
    return imageSrc ? [imageSrc] : []
  }, [imageSrc, imageSrcs])

  const mobileSlides = React.useMemo(() => {
    const list = mobileImageSrcs?.filter(Boolean) || []
    if (list.length > 0) return list
    if (mobileImageSrc) return [mobileImageSrc]
    return desktopSlides
  }, [desktopSlides, mobileImageSrc, mobileImageSrcs])

  React.useEffect(() => {
    setCurrentIndex(0)
  }, [desktopSlides.length, mobileSlides.length])

  React.useEffect(() => {
    if (typeof window === "undefined") return
    setDismissed(window.sessionStorage.getItem(sessionKey) === "true")
    setReady(true)
  }, [sessionKey])

  React.useEffect(() => {
    if (desktopSlides.length <= 1 && mobileSlides.length <= 1) return
    const timer = window.setInterval(() => {
      const total = Math.max(desktopSlides.length, mobileSlides.length)
      setCurrentIndex((old) => (old + 1) % total)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [desktopSlides.length, mobileSlides.length])

  const onClose = React.useCallback(() => {
    setDismissed(true)
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(sessionKey, "true")
    }
  }, [sessionKey])

  if (!ready || dismissed || desktopSlides.length === 0) return null

  return (
    <Wrapper aria-label="Floating promo banner">
      <Picture>
        <source
          media="(max-width: 479px)"
          srcSet={mobileSlides[currentIndex % mobileSlides.length]}
        />
        <PromoImage
          src={desktopSlides[currentIndex % desktopSlides.length]}
          alt={alt}
          decoding="async"
        />
      </Picture>
      {Math.max(desktopSlides.length, mobileSlides.length) > 1 && (
        <Dots>
          {Array.from({
            length: Math.max(desktopSlides.length, mobileSlides.length),
          }).map((_, index) => (
            <Dot
              key={`floating-promo-dot-${index}`}
              type="button"
              active={index === currentIndex}
              aria-label={`View promo banner ${index + 1}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </Dots>
      )}

      <CloseButton onClick={onClose} aria-label="Close promo banner">
        <Close strokeColor={colors.snow} />
      </CloseButton>
    </Wrapper>
  )
}

export default FloatingPromoBanner
